import { QrcodeOutlined } from '@ant-design/icons';
import {
  getGetFrontRichiesteIdPagamentiIdAvvisoQueryOptions,
  getGetFrontRichiesteIdPagamentiQueryKey,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type PagamentoWithDettaglioDTO } from '@framework/api-client/api/inquiries-compile/schemas/pagamentoWithDettaglioDTO.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useHasDocumentsInlineViewer } from '@framework/common-web/ui/documents/useHasDocumentsInlineViewer.tsx';
import { useAuthDownload } from '@framework/common-web/utils/download/useAuthDownload.ts';
import { Button, type ButtonProps, message, Tooltip } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { EnumCodStatoPagamento } from '../../burden/payments/EnumCodStatoPagamento.tsx';
import {
  hasPaymentAlertMethod,
  isPaymentAlertDocumentPending,
} from '../utils/paymentAlertState.ts';

interface DownloadPaymentAlertButtonProps {
  payment: PagamentoWithDettaglioDTO;
  type?: ButtonProps['type'];
  size?: ButtonProps['size'];
}

export function DownloadPaymentAlertButton(
  props: DownloadPaymentAlertButtonProps,
) {
  const { payment, type, size } = props;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const hasInlineViewer = useHasDocumentsInlineViewer();

  const invalidatePayments = useCallback(() => {
    queryClient.invalidateQueries(
      getGetFrontRichiesteIdPagamentiQueryKey(payment.idRichiesta!),
    );
  }, [queryClient, payment.idRichiesta]);

  const { download, isFetching } = useAuthDownload(
    getGetFrontRichiesteIdPagamentiIdAvvisoQueryOptions,
    [payment.idRichiesta!, payment.id!],
    {
      onSuccess: invalidatePayments,
      onError: e => {
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'form.errore-in-download',
              'Si è verificato un problema durante il download del file',
            ),
          ),
        );
      },
      showInlineViewer: hasInlineViewer,
    },
  );

  const handleClick = () => {
    // In modalità asincrona l'avviso è disponibile solo quando il servizio
    // di pagamento ha fornito lo IUV
    if (isPaymentAlertDocumentPending(payment)) {
      message.info(
        t(
          'form.avviso-non-ancora-disponibile',
          'Documento non ancora disponibile, riprovare tra qualche minuto',
        ),
      );
      invalidatePayments();
      return;
    }
    download();
  };

  if (!hasPaymentAlertMethod(payment)) return null;
  if (payment.codStatoPagamento === EnumCodStatoPagamento.SUCCESSO) return null;

  return (
    <Tooltip title={t('form.scarica-avviso', 'Scarica Avviso')}>
      <Button
        icon={
          <QrcodeOutlined
            aria-label={t('form.scarica-avviso', 'Scarica Avviso')}
          />
        }
        onClick={handleClick}
        disabled={!payment.id || !payment.idRichiesta}
        loading={isFetching}
        size={size}
        type={type}
      />
    </Tooltip>
  );
}
